/**
 * V11_CONFIG Runtime Override Store — P-14 (Audit Verbeterplan v4.x)
 *
 * Allows admin to configure adaptive engine parameters at runtime
 * without code changes. Enables A/B testing of pedagogical thresholds.
 *
 * ARCHITECTURE
 * ============
 * V11_CONFIG is a `const` object whose properties are read at call-time
 * by all consumers (e.g. `V11_CONFIG.STRUGGLING_THRESHOLD`). Because
 * properties are accessed (not destructured/captured), mutating those
 * properties at runtime is picked up by all consumers immediately.
 *
 * This store:
 *   1. Captures the original defaults on first load (DEFAULT_V11_CONFIG)
 *   2. Loads persisted overrides from dataLayer at app boot
 *   3. Mutates V11_CONFIG via Object.assign to apply overrides
 *   4. Provides save/reset APIs for the admin settings UI
 *
 * SAFETY
 * ======
 * - All overrides are validated against allowed keys + numeric ranges.
 * - Invalid values are silently ignored (logs a warning).
 * - Reset always restores the original defaults.
 * - Failures to load are non-fatal: defaults remain in effect.
 */

import { V11_CONFIG } from './selectionConfig';
import { dataLayer } from '../../data/dataLayer';
import { warn, debug } from '../../utils/logger';

// ─── Defaults Snapshot ─────────────────────────────────────────────────────

/**
 * Frozen snapshot of the original V11_CONFIG values at module load.
 * Used by reset() to restore defaults.
 *
 * Structured clone via JSON ensures nested objects (MIN_DOK_FOR_TIER_BAND)
 * are isolated from runtime mutations.
 */
// Cross-cluster RC: structuredClone is native (Node 17+, all evergreen
// browsers since 2022) and avoids JSON round-trip pitfalls (undefined/NaN
// loss, Date→string, no Map/Set support). The V11_CONFIG shape is plain
// JSON-compatible today but structuredClone is forward-compatible if any
// field ever needs richer types.
const DEFAULT_V11_CONFIG: typeof V11_CONFIG = structuredClone(V11_CONFIG);
export function getDefaultV11Config(): typeof V11_CONFIG {
  return structuredClone(DEFAULT_V11_CONFIG);
}

// ─── Configurable Keys ─────────────────────────────────────────────────────

/**
 * Keys that the admin UI is allowed to override.
 * Each key has a min/max validation range and a human-readable label.
 *
 * NOTE: MIN_DOK_FOR_TIER_BAND is intentionally NOT included here — it's a
 * nested object with pedagogical invariants that should not be tweaked
 * via simple numeric inputs. Same for MIN_TIER/MAX_TIER (system constants).
 */
export interface V11ConfigField {
  key: keyof typeof V11_CONFIG;
  label: string;
  description: string;
  group: 'mastery' | 'session' | 'tier' | 'pedagogy' | 'diversity';
  min: number;
  max: number;
  step?: number;
  default: number;
}
export const V11_CONFIG_FIELDS: V11ConfigField[] = [
// Mastery thresholds
{
  key: 'STRUGGLING_THRESHOLD',
  label: 'Struggling drempel',
  description: 'Mastery score waaronder een family als "struggling" geldt',
  group: 'mastery',
  min: 0,
  max: 100,
  default: 40
}, {
  key: 'LEARNING_THRESHOLD',
  label: 'Learning drempel',
  description: 'Mastery score waaronder een family als "learning" geldt',
  group: 'mastery',
  min: 0,
  max: 100,
  default: 70
}, {
  key: 'MASTERED_THRESHOLD',
  label: 'Mastered drempel',
  description: 'Mastery score waarboven een family als "mastered" geldt',
  group: 'mastery',
  min: 0,
  max: 100,
  default: 85
}, {
  key: 'MASTERY_CONFIDENCE_MIN_ATTEMPTS',
  label: 'Min. pogingen voor mastery vertrouwen',
  description: 'Aantal pogingen vóór mastery als stabiel geldt',
  group: 'mastery',
  min: 1,
  max: 20,
  default: 5
}, {
  key: 'LDG_PREREQUISITE_MASTERY',
  label: 'LDG prerequisite drempel',
  description: 'Mastery vereist op prerequisite vóór gating opent',
  group: 'mastery',
  min: 0,
  max: 100,
  default: 60
}, {
  key: 'PLUS_UNLOCK_MASTERY',
  label: 'Plus-tier unlock drempel',
  description: 'Mastery op base tier vóór plus-tier in window verschijnt',
  group: 'mastery',
  min: 0,
  max: 100,
  default: 55
},
// Session shape
{
  key: 'MAX_QUESTIONS_PER_FAMILY',
  label: 'Max vragen per family',
  description: 'Maximaal aantal vragen uit dezelfde family per sessie',
  group: 'session',
  min: 1,
  max: 10,
  default: 2
}, {
  key: 'MIN_FAMILIES_PER_SESSION',
  label: 'Min. families per sessie',
  description: 'Minimaal aantal verschillende families (indien beschikbaar)',
  group: 'session',
  min: 1,
  max: 10,
  default: 2
}, {
  key: 'MAX_MASTERED_PER_SESSION',
  label: 'Max mastered families per sessie',
  description: 'Soft cap op aantal mastered families in een sessie',
  group: 'session',
  min: 0,
  max: 10,
  default: 2
}, {
  key: 'MAX_MASTERED_PERCENTAGE',
  label: 'Max % mastered content',
  description: 'Max percentage van sessie dat mastered content mag zijn',
  group: 'session',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.7
}, {
  key: 'TOP_CANDIDATE_POOL',
  label: 'Top kandidaat pool',
  description: 'Aantal top families waaruit willekeurig wordt gekozen',
  group: 'session',
  min: 1,
  max: 10,
  default: 4
}, {
  key: 'RECENT_FAMILY_COOLDOWN',
  label: 'Recent family cooldown',
  description: 'Aantal recent gebruikte families dat lager wordt geprioriteerd',
  group: 'session',
  min: 0,
  max: 20,
  default: 5
}, {
  key: 'RECENCY_PENALTY_MULTIPLIER',
  label: 'Recency penalty multiplier',
  description: 'Priority multiplier voor recent gebruikte families (0.5 = -50%)',
  group: 'session',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.5
},
// Tier window
{
  key: 'TIER_WINDOW_BELOW',
  label: 'Tier window onder',
  description: 'Aantal tiers onder target opgenomen in window',
  group: 'tier',
  min: 0,
  max: 5,
  default: 1
}, {
  key: 'TIER_WINDOW_ABOVE',
  label: 'Tier window boven',
  description: 'Aantal tiers boven target opgenomen in window',
  group: 'tier',
  min: 0,
  max: 5,
  default: 1
}, {
  key: 'TIER_WINDOW_FALLBACK',
  label: 'Tier window fallback',
  description: 'Uitgebreid window bij fallback pogingen',
  group: 'tier',
  min: 1,
  max: 10,
  default: 3
}, {
  key: 'LEVEL_UNLOCK_THRESHOLD',
  label: 'Level unlock drempel',
  description: 'Ratio mastered/practiced nodig voor level unlock',
  group: 'tier',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.5
},
// BME
{
  key: 'BME_PROXIMITY_BOOST_EXACT',
  label: 'B/M/E boost (exact match)',
  description: 'Priority bonus bij exacte B/M/E tier match',
  group: 'pedagogy',
  min: 0,
  max: 50,
  default: 20
}, {
  key: 'BME_PROXIMITY_BOOST_NEAR',
  label: 'B/M/E boost (±1 tier)',
  description: 'Priority bonus binnen 1 tier van B/M/E',
  group: 'pedagogy',
  min: 0,
  max: 50,
  default: 10
}, {
  key: 'BME_PROXIMITY_BOOST_FAR',
  label: 'B/M/E boost (±2 tier)',
  description: 'Priority bonus binnen 2 tiers van B/M/E',
  group: 'pedagogy',
  min: 0,
  max: 50,
  default: 5
},
// Diversity
{
  key: 'MAX_CONSECUTIVE_MC',
  label: 'Max opeenvolgende MC vragen',
  description: 'Na zoveel MC-vragen wordt interactieve voorkeur geactiveerd',
  group: 'diversity',
  min: 1,
  max: 10,
  default: 3
}, {
  key: 'SKILL_INTERLEAVE_WINDOW',
  label: 'Skill interleave window',
  description: 'Aantal recente skills gecheckt voor interleaving',
  group: 'diversity',
  min: 0,
  max: 10,
  default: 2
}, {
  key: 'SKILL_INTERLEAVE_PENALTY',
  label: 'Skill interleave penalty',
  description: 'Priority multiplier bij skill match (0.6 = -40%)',
  group: 'diversity',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.6
}, {
  key: 'COGNITIVE_DIVERSITY_WINDOW',
  label: 'Cognitive diversity window',
  description: 'Aantal recente cognitive levels gecheckt voor diversiteit',
  group: 'diversity',
  min: 0,
  max: 10,
  default: 3
}, {
  key: 'FRUSTRATION_CONSECUTIVE_ERRORS',
  label: 'Frustratie drempel',
  description: 'Aantal opeenvolgende fouten op zelfde family vóór swap (0 = uit)',
  group: 'pedagogy',
  min: 0,
  max: 10,
  default: 3
},
// Mid-session difficulty adjustment (P-13)
{
  key: 'DIFFICULTY_ADJUST_WINDOW',
  label: 'Difficulty adjust window',
  description: 'Aantal recente antwoorden waarover accuracy wordt berekend voor mid-session aanpassing',
  group: 'pedagogy',
  min: 3,
  max: 15,
  default: 5
}, {
  key: 'DIFFICULTY_ADJUST_UP_THRESHOLD',
  label: 'Difficulty omhoog drempel',
  description: 'Accuracy boven deze drempel → vervang komende vragen door moeilijkere (0 = uit)',
  group: 'pedagogy',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.85
}, {
  key: 'DIFFICULTY_ADJUST_DOWN_THRESHOLD',
  label: 'Difficulty omlaag drempel',
  description: 'Accuracy onder deze drempel → vervang komende vragen door makkelijkere (0 = uit)',
  group: 'pedagogy',
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.35
}, {
  key: 'DIFFICULTY_ADJUST_TIER_SHIFT',
  label: 'Difficulty tier shift',
  description: 'Hoeveel tiers omhoog/omlaag bij aanpassing',
  group: 'pedagogy',
  min: 1,
  max: 4,
  default: 1
}, {
  key: 'DIFFICULTY_ADJUST_COOLDOWN',
  label: 'Difficulty adjust cooldown',
  description: 'Minimum antwoorden tussen twee aanpassingen (voorkomt oscillatie)',
  group: 'pedagogy',
  min: 1,
  max: 15,
  default: 4
}];
const FIELD_BY_KEY = new Map(V11_CONFIG_FIELDS.map(f => [f.key, f]));

// ─── State ────────────────────────────────────────────────────────────────

export type V11ConfigOverrides = Partial<Record<keyof typeof V11_CONFIG, number>>;
let currentOverrides: V11ConfigOverrides = {};
let loaded = false;

// ─── Validation ───────────────────────────────────────────────────────────

function validateOverrides(input: unknown): V11ConfigOverrides {
  if (!input || typeof input !== 'object') return {};
  const out: V11ConfigOverrides = {};
  const obj = input as Record<string, unknown>;
  for (const [key, raw] of Object.entries(obj)) {
    const field = FIELD_BY_KEY.get(key as keyof typeof V11_CONFIG);
    if (!field) {
      // unknown / not user-configurable — ignore silently
      continue;
    }
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      warn(`[V11ConfigStore] Invalid value for ${key}: ${raw} (not a number)`);
      continue;
    }
    if (raw < field.min || raw > field.max) {
      warn(`[V11ConfigStore] Value out of range for ${key}: ${raw} (allowed ${field.min}–${field.max})`);
      continue;
    }
    out[field.key] = raw;
  }
  return out;
}

// ─── Apply / Reset Mutations ──────────────────────────────────────────────

function applyToV11Config(overrides: V11ConfigOverrides) {
  // First reset all configurable keys to their defaults, then apply overrides.
  // This ensures removing an override returns the value to its default.
  for (const field of V11_CONFIG_FIELDS) {
    ;
    (V11_CONFIG as Record<string, unknown>)[field.key] = (DEFAULT_V11_CONFIG as Record<string, unknown>)[field.key];
  }
  for (const [key, value] of Object.entries(overrides)) {
    if (value === undefined) continue;
    (V11_CONFIG as Record<string, unknown>)[key] = value;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Load persisted overrides from dataLayer and apply them to V11_CONFIG.
 * Safe to call multiple times — only the first call hits storage.
 */
export async function loadV11ConfigOverrides(): Promise<V11ConfigOverrides> {
  if (loaded) return {
    ...currentOverrides
  };
  loaded = true;
  try {
    const raw = await dataLayer.getAdaptiveConfig();
    if (raw && typeof raw === 'object') {
      const overrides = validateOverrides((raw as {
        overrides?: unknown;
      }).overrides ?? raw);
      currentOverrides = overrides;
      applyToV11Config(currentOverrides);
      const count = Object.keys(currentOverrides).length;
      if (count > 0) {
        debug(`[V11ConfigStore] Applied ${count} runtime overrides to V11_CONFIG`);
      }
    }
  } catch (e) {
    warn('[V11ConfigStore] Failed to load adaptive config — using defaults');
  }
  return {
    ...currentOverrides
  };
}

/**
 * Get the currently active overrides (subset of V11_CONFIG keys).
 */
export function getV11ConfigOverrides(): V11ConfigOverrides {
  return {
    ...currentOverrides
  };
}

/**
 * Save new overrides, validate them, persist via dataLayer, and apply
 * them to V11_CONFIG.
 *
 * Returns the cleaned overrides that were actually applied.
 */
export async function saveV11ConfigOverrides(overrides: V11ConfigOverrides): Promise<V11ConfigOverrides> {
  const cleaned = validateOverrides(overrides);
  currentOverrides = cleaned;
  applyToV11Config(cleaned);
  try {
    await dataLayer.saveAdaptiveConfig({
      version: 1,
      overrides: cleaned,
      lastSaved: Date.now()
    });
    debug(`[V11ConfigStore] Saved ${Object.keys(cleaned).length} overrides to storage`);
  } catch (e) {
    warn('[V11ConfigStore] Failed to persist adaptive config');
  }
  return {
    ...cleaned
  };
}

/**
 * Reset to defaults — clears all overrides from storage and restores
 * V11_CONFIG to its original values.
 */
export async function resetV11ConfigOverrides(): Promise<void> {
  currentOverrides = {};
  applyToV11Config({});
  try {
    await dataLayer.resetAdaptiveConfig();
    debug('[V11ConfigStore] Reset adaptive config to defaults');
  } catch (e) {
    warn('[V11ConfigStore] Failed to reset adaptive config');
  }
}